<template>
  <teleport to="body">
    <div class="ci" :style="cssVars" ref="ciEl">

      <!-- Scrollbar customizado -->
      <div
        v-if="showGiftPanel && isScrollable"
        class="ci__scrollbar"
        @pointerdown="_onScrollbarTrackPointerDown"
      >
        <div
          class="ci__scrollbar-thumb"
          :style="scrollThumbStyle"
          @pointerdown.stop="_onScrollbarThumbPointerDown"
        ></div>
      </div>

      <!-- Fase 1: spinner -->
      <transition name="ci-fade" @after-leave="cardVisible = true">
        <div v-if="spinnerVisible" class="ci__loader">
          <div class="ci__spinner" role="status">
            <span class="visually-hidden">Carregando...</span>
          </div>
        </div>
      </transition>

      <!-- Fase 2: convite -->
      <transition name="ci-reveal">
        <div v-if="cardVisible" class="ci__scene" :class="{ 'ci__scene--gift': showGiftPanel }">

          <!-- Fundo: foto com gradiente -->
          <div class="ci__bg">
            <img
              v-if="bgImage || mainPhoto"
              :src="bgImage || mainPhoto"
              class="ci__bg-img"
              :style="{ opacity: bgOpacity }"
              alt=""
            />
            <div class="ci__bg-overlay" />
          </div>

          <!-- Conteúdo -->
          <div class="ci__body" :class="{ 'ci__body--gift': showGiftPanel }">

            <!-- Tipo do evento (badge) -->
            <span v-if="eventType" class="ci__badge">{{ eventType }}</span>

            <!-- Título -->
            <h1 class="ci__title">{{ eventName }}</h1>

            <!-- Conteúdo intercambiável: convite ↔ lista -->
            <transition name="ci-panel-slide" mode="out-in" @after-enter="_onPanelEntered">

              <!-- ESTADO: Convite -->
              <div v-if="!showGiftPanel" key="invite" class="ci__invite-content">

                <!-- Divisor decorativo -->
                <div class="ci__divider">
                  <span class="ci__divider-gem">✦</span>
                </div>

            <!-- Bloco de data estilizado -->
            <div v-if="dateParts" class="ci__datebox">
              <div class="ci__datebox-col">
                <span class="ci__datebox-month">{{ dateParts.month }}</span>
                <span class="ci__datebox-weekday">{{ dateParts.weekday }}</span>
              </div>
              <div class="ci__datebox-sep"></div>
              <div class="ci__datebox-col ci__datebox-col--center">
                <span class="ci__datebox-day">{{ dateParts.day }}</span>
                <span v-if="dateParts.time" class="ci__datebox-time">às {{ dateParts.time }}</span>
              </div>
              <div class="ci__datebox-sep"></div>
              <div class="ci__datebox-col">
                <span class="ci__datebox-year">{{ dateParts.year }}</span>
              </div>
            </div>

            <!-- Demais detalhes -->
            <div v-if="location || dressCode" class="ci__details">
              <div v-if="location" class="ci__detail">
                <span class="ci__detail-icon"><i class="fas fa-location-dot"></i></span>
                <span>{{ location }}</span>
              </div>
              <div v-if="dressCode" class="ci__detail">
                <span class="ci__detail-icon"><i class="fas fa-shirt"></i></span>
                <span>{{ dressCode }}</span>
              </div>
            </div>

            <!-- Galeria de fotos (strip horizontal clicável) -->
            <div v-if="allPhotos.length > 1" class="ci__strip">
              <div
                v-for="(photo, i) in allPhotos"
                :key="i"
                class="ci__strip-cell"
                :style="{ animationDelay: (i * 80) + 'ms' }"
                @click="openLightbox(i)"
              >
                <img :src="photo" :alt="'Foto ' + (i + 1)" />
                <div class="ci__strip-cell-overlay"><i class="fas fa-expand"></i></div>
              </div>
            </div>

            <!-- Confetes (gerados por JS) -->
            <div class="ci__confetti" aria-hidden="true">
              <span
                v-for="c in confetti"
                :key="c.id"
                class="ci__confetti-piece"
                :style="c.style"
              />
            </div>

                <!-- CTA principal -->
                <button class="ci__cta" @click="$slots['gift-panel'] ? showGiftPanel = true : $emit('close')">
                  <i class="fas fa-gift me-2"></i>
                  Ver lista de presentes
                </button>

                <!-- CTAs secundários: Pix + RSVP inline no desktop -->
                <div class="ci__secondary-row" :class="{ 'ci__secondary-row--no-rsvp': !hasRsvp }">
                  <button class="ci__cta-pix" @click="pixSheetOpen = true">
                    <i class="fas fa-qrcode me-1"></i>
                    Contribua com Pix
                  </button>

              <template v-if="hasRsvp">
                <template v-if="rsvpStep !== 'success'">
                  <button class="ci__rsvp-link" @click="_openRsvpSheet">
                    <i class="fas fa-user-check me-1"></i> Confirmar minha presença
                  </button>
                </template>
                  <div v-else class="ci__rsvp-confirmed-badge">
                    <i class="fas fa-check-circle me-2"></i> Presença confirmada!
                  </div>
                </template>
              </div>

              </div><!-- /ci__invite-content -->

              <!-- ESTADO: Lista de Presentes -->
              <div v-else key="gifts" class="ci__gift-panel">
                <div class="ci__gift-handle" @click="showGiftPanel = false">
                  <div class="ci__gift-handle-pill"></div>
                  <span class="ci__gift-handle-label">
                    <i class="fas fa-chevron-down me-1"></i>Ver convite
                  </span>
                </div>
                <slot name="gift-panel" />
              </div>

            </transition><!-- /ci-panel-slide -->

          </div><!-- /ci__body -->

          <!-- Botão flutuante de personalização — apenas para o dono -->
          <button
            v-if="isOwner && !showGiftPanel"
            class="ci__fab-customize"
            @click="$emit('open-customize')"
            aria-label="Personalizar convite"
          >
            <i class="fas fa-palette"></i>
          </button>

          <!-- Rodapé de marca — oculto quando white label está ativo -->
          <div v-if="!whiteLabel" class="ci__brand-footer">
            <span>Feito com</span>
            <i class="fas fa-heart ci__brand-footer__heart"></i>
            <span>por <router-link to="/" class="ci__brand-footer__link"><strong>QueMimo</strong></router-link></span>
          </div>

        </div><!-- /ci__scene -->
      </transition>

      <!-- ── Bottom Sheet Pix ─────────────────── -->
      <transition name="ci-sheet">
        <div v-if="pixSheetOpen" class="ci__sheet-backdrop" @click.self="_tryClosePixSheet">
          <div class="ci__sheet">
            <button class="ci__sheet-close" @click="_tryClosePixSheet">
              <i class="fas fa-times"></i>
            </button>

            <!-- STEP: form -->
            <template v-if="pixStep === 'form'">
              <CheckoutIdentification
                method="pix-livre"
                :event-id="eventId"
                :primary-color="resolvedColor"
                @identified="onPixLivreIdentified"
              />
            </template>

            <!-- Overlay de confirmação de saída -->
            <div v-if="pixCloseConfirm" class="ci__sheet-confirm-overlay">
              <p class="ci__sheet-confirm-title">Sair do pagamento?</p>
              <p class="ci__sheet-confirm-sub">O QR Code gerado será perdido e você precisará gerar um novo.</p>
              <div class="ci__sheet-confirm-actions">
                <button class="ci__sheet-confirm-btn ci__sheet-confirm-btn--danger" @click="_forceClosePixSheet">Sim, sair</button>
                <button class="ci__sheet-confirm-btn ci__sheet-confirm-btn--cancel" @click="pixCloseConfirm = false">Continuar pagando</button>
              </div>
            </div>

            <!-- STEP: qr -->
            <template v-else-if="pixStep === 'qr'">
              <CheckoutPixPayment
                :event-id="eventId"
                :customer="pixLivreCustomer"
                :total="pixLivreValue"
                :items="pixLivreItems"
                :primary-color="resolvedColor"
                @success="onPixLivreSuccess"
                @back="pixStep = 'form'"
              />
            </template>

            <!-- STEP: success -->
            <template v-else-if="pixStep === 'success'">
              <div class="ci__sheet-paid">
                <i class="fas fa-check-circle" style="font-size:3rem;color:#22c55e"></i>
                <p class="mt-3 fw-bold">Contribuição recebida!</p>
                <p class="ci__sheet-paid-sub">Obrigado pela sua generosidade 💕</p>
                <button class="ci__sheet-success-btn" @click="pixSheetOpen = false">Voltar ao convite</button>
              </div>
            </template>

          </div>
        </div>
      </transition>

      <!-- ── Bottom Sheet RSVP ────────────────────── -->
      <transition name="ci-sheet">
        <div v-if="rsvpSheetOpen" class="ci__sheet-backdrop" @click.self="_closeRsvpSheet">
          <div class="ci__sheet ci__sheet--rsvp">
            <button class="ci__sheet-close" @click="_closeRsvpSheet">
              <i class="fas fa-times"></i>
            </button>

            <!-- STEP: code — sempre o primeiro passo -->
            <template v-if="rsvpStep === 'code'">
              <div class="ci__rsvp-icon">🎟️</div>
              <p class="ci__sheet-eyebrow">Lista de presença</p>
              <h3 class="ci__sheet-title">Informe seu código</h3>
              <p class="ci__sheet-sub">O código foi enviado junto com o convite.</p>

              <div class="ci__sheet-field">
                <label>Código do convite <span class="ci__required">*</span></label>
                <div class="ci__rsvp-code-row">
                  <input
                    v-model="rsvpCodeInput"
                    type="text"
                    placeholder="Ex: T82U"
                    maxlength="4"
                    autocomplete="off"
                    @input="rsvpCodeInput = rsvpCodeInput.toUpperCase(); rsvpError = ''"
                    @keyup.enter="_lookupRsvpCode"
                  />
                  <button
                    class="ci__rsvp-code-btn"
                    @click="_lookupRsvpCode"
                    :disabled="rsvpCodeLookingUp || !rsvpCodeInput.trim()"
                  >
                    <span v-if="rsvpCodeLookingUp" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="fas fa-arrow-right"></i>
                  </button>
                </div>
                <p v-if="rsvpError" class="ci__sheet-error ci__sheet-error--inline">{{ rsvpError }}</p>
              </div>
            </template>

            <!-- STEP: confirm — quantidade e mensagem -->
            <template v-else-if="rsvpStep === 'confirm'">
              <div class="ci__rsvp-icon">🎉</div>
              <p class="ci__sheet-eyebrow">Lista de presença</p>
              <h3 class="ci__sheet-title">Olá, {{ rsvpGroupName }}!</h3>
              <p class="ci__sheet-sub">Confirme sua presença em <strong>{{ eventName }}</strong>.</p>

              <div class="ci__sheet-field">
                <label>Quantas pessoas vão? <span class="ci__required">*</span></label>
                <div class="ci__rsvp-count-row">
                  <button class="ci__rsvp-count-btn" @click="rsvpConfirmedCount = Math.max(1, rsvpConfirmedCount - 1)">−</button>
                  <span class="ci__rsvp-count-val">{{ rsvpConfirmedCount }}</span>
                  <button class="ci__rsvp-count-btn" @click="rsvpConfirmedCount = Math.min(rsvpInviteCount, rsvpConfirmedCount + 1)">+</button>
                  <span class="ci__rsvp-count-max">de {{ rsvpInviteCount }}</span>
                </div>
              </div>

              <div class="ci__sheet-field" style="margin-top: 1.25rem;">
                <label>Mensagem <span class="ci__optional">(opcional)</span></label>
                <textarea
                  v-model="rsvpMessage"
                  class="ci__rsvp-textarea"
                  placeholder="Ex: Mal posso esperar! 🥳"
                  rows="2"
                  maxlength="500"
                ></textarea>
              </div>

              <p v-if="rsvpError" class="ci__sheet-error ci__sheet-error--inline">{{ rsvpError }}</p>

              <button class="ci__sheet-copy ci__sheet-copy--rsvp" @click="_confirmarRsvp">
                <i class="fas fa-check me-2"></i>
                Confirmar presença
              </button>
              <button class="ci__sheet-back" @click="rsvpStep = 'code'; rsvpGroupName = ''; rsvpError = ''">
                <i class="fas fa-chevron-left me-1"></i> Alterar código
              </button>
            </template>

            <!-- STEP: loading -->
            <template v-else-if="rsvpStep === 'loading'">
              <div class="ci__sheet-paid">
                <span class="spinner-border" style="width:2.5rem;height:2.5rem;color:var(--ci-primary)"></span>
                <p class="mt-3" style="color:rgba(255,255,255,0.7)">Confirmando...</p>
              </div>
            </template>

            <!-- STEP: success -->
            <template v-else-if="rsvpStep === 'success'">
              <div class="ci__sheet-paid">
                <i class="fas fa-check-circle" style="font-size:3rem;color:#22c55e"></i>
                <p class="mt-3 fw-bold">Presença confirmada!</p>
                <p class="ci__sheet-paid-sub">Até lá, {{ rsvpGroupName }}! 🥳</p>
                <button class="ci__sheet-success-btn" @click="_closeRsvpSheet">Voltar ao convite</button>
              </div>
            </template>

          </div>
        </div>
      </transition>

    </div><!-- /.ci -->

    <!-- ── Bottom Sheet Carrinho ────────────────── -->
    <transition name="ci-sheet">
      <div v-if="cartSheetOpen" class="ci__sheet-backdrop" :style="cssVars" @click.self="tryCloseCartSheet">
        <div class="ci__sheet ci__sheet--cart">

          <!-- Overlay de confirmação de saída -->
          <div v-if="cartCloseConfirm" class="ci__sheet-confirm-overlay">
            <p class="ci__sheet-confirm-title">Sair do pagamento?</p>
            <p class="ci__sheet-confirm-sub">{{ cartCloseConfirmMessage }}</p>
            <div class="ci__sheet-confirm-actions">
              <button class="ci__sheet-confirm-btn ci__sheet-confirm-btn--danger" @click="forceCloseCartSheet">Sim, sair</button>
              <button class="ci__sheet-confirm-btn ci__sheet-confirm-btn--cancel" @click="cartCloseConfirm = false">Continuar pagando</button>
            </div>
          </div>

          <!-- Botão fechar -->
          <button class="ci__sheet-close" @click="tryCloseCartSheet">
            <i class="fas fa-times"></i>
          </button>

          <!-- ─── Step: Resumo ─── -->
          <template v-if="cartStep === 'summary'">
            <p class="ci__sheet-eyebrow">Seus presentes</p>
            <h3 class="ci__sheet-title">Resumo do carrinho</h3>

            <div class="ci__cart-items">
              <div v-for="item in cartItems" :key="item.id" class="ci__cart-item">
                <img v-if="item.photo_url" :src="item.photo_url" class="ci__cart-item-img" :alt="item.name" />
                <div v-else class="ci__cart-item-img ci__cart-item-img--placeholder">
                  <i class="fas fa-gift"></i>
                </div>
                <div class="ci__cart-item-info">
                  <span class="ci__cart-item-name">{{ item.name }}</span>
                  <span class="ci__cart-item-price">R$ {{ formatCurrency(item.price) }}</span>
                </div>
              </div>
            </div>

            <div class="ci__cart-total">
              <span>Total</span>
              <strong>R$ {{ formatCurrency(cartTotal) }}</strong>
            </div>

            <div class="ci__cart-actions">
              <button
                class="ci__cart-btn ci__cart-btn--pix"
                @click="goToIdentification('pix')"
              >
                <i class="fas fa-qrcode me-2"></i>Pagar com Pix
              </button>
              <button
                class="ci__cart-btn ci__cart-btn--card"
                @click="goToIdentification('card')"
              >
                <i class="fas fa-credit-card me-2"></i>Pagar com Cartão
              </button>
            </div>
          </template>

          <!-- ─── Step: Identificação ─── -->
          <template v-else-if="cartStep === 'identification'">
            <CheckoutIdentification
              :method="cartPaymentMethod"
              :event-id="eventId"
              :primary-color="resolvedColor"
              @identified="onCartIdentified"
            />
            <button class="ci__cart-back-btn" @click="cartStep = 'summary'">
              <i class="fas fa-chevron-left me-1"></i>Voltar
            </button>
          </template>

          <!-- ─── Step: Pix Payment ─── -->
          <template v-else-if="cartStep === 'pix-payment'">
            <CheckoutPixPayment
              :event-id="eventId"
              :customer="cartCustomer"
              :items="cartItems"
              :total="cartTotal"
              :primary-color="resolvedColor"
              @success="onCartPixSuccess"
              @back="cartStep = 'identification'"
            />
          </template>

          <!-- ─── Step: Card Payment ─── -->
          <template v-else-if="cartStep === 'card-payment'">
            <CheckoutCardPayment
              :event-id="eventId"
              :customer="cartCustomer"
              :items="cartItems"
              :total="cartTotal"
              :primary-color="resolvedColor"
              @success="onCartCardSuccess"
              @back="goBackFromCardPayment"
              @use-pix="onCartSwitchToPix"
              @state-change="onCartCardStateChange"
            />
          </template>

          <!-- ─── Step: Sucesso ─── -->
          <template v-else-if="cartStep === 'pix-success'">
            <div class="ci__sheet-paid">
              <div class="ci__sheet-paid-icon">
                <i class="fas fa-check-circle"></i>
              </div>
              <p class="ci__sheet-paid-title">Pagamento confirmado!</p>
              <p class="ci__sheet-paid-sub">Seu presente foi registrado com sucesso 🎉</p>
              <button class="ci__sheet-success-btn" @click="closeCartSheet">Voltar ao convite</button>
            </div>
          </template>

        </div>
      </div>
    </transition>

    <!-- ── Lightbox ────────────────────────────── -->
    <transition name="ci-lb">
      <div
        v-if="lightboxOpen"
        class="ci__lb"
        :style="cssVars"
        @touchstart.passive="lbTouchStart"
        @touchend.passive="lbTouchEnd"
      >
        <!-- Fechar -->
        <button class="ci__lb-close" @click="lightboxOpen = false">
          <i class="fas fa-times"></i>
        </button>

        <!-- Foto atual -->
        <div class="ci__lb-img-wrap">
          <transition :name="lbSlideDir" mode="out-in">
            <img
              :key="lbIndex"
              :src="allPhotos[lbIndex]"
              class="ci__lb-img"
              :alt="'Foto ' + (lbIndex + 1)"
            />
          </transition>
        </div>

        <!-- Prev / Next -->
        <button v-if="allPhotos.length > 1" class="ci__lb-nav ci__lb-nav--prev" @click="lbPrev">
          <i class="fas fa-chevron-left"></i>
        </button>
        <button v-if="allPhotos.length > 1" class="ci__lb-nav ci__lb-nav--next" @click="lbNext">
          <i class="fas fa-chevron-right"></i>
        </button>

        <!-- Dots -->
        <div v-if="allPhotos.length > 1" class="ci__lb-dots">
          <span
            v-for="(_, i) in allPhotos"
            :key="i"
            class="ci__lb-dot"
            :class="{ 'ci__lb-dot--active': i === lbIndex }"
            @click="lbGoto(i)"
          />
        </div>

        <!-- Contador -->
        <span class="ci__lb-counter">{{ lbIndex + 1 }} / {{ allPhotos.length }}</span>
      </div>
    </transition>

  </teleport>
</template>

<script>
import api from '@/services/api';
import { getFees } from '@/services/feesService';
import { toast } from 'vue3-toastify';
import { useCartStore } from '@/stores/cartStore';
import { formatCurrency } from '@/utils/currencyFormatter';
import CheckoutIdentification from '@/components/checkout/CheckoutIdentification.vue';
import CheckoutPixPayment from '@/components/checkout/CheckoutPixPayment.vue';
import CheckoutCardPayment from '@/components/checkout/CheckoutCardPayment.vue';

export default {
  name: 'ConviteComponent',

  components: { CheckoutIdentification, CheckoutPixPayment, CheckoutCardPayment },

  emits: ['close', 'cart-sheet-close', 'open-customize', 'rsvp-confirmed'],

  props: {
    eventName:    { type: String,  default: '' },
    eventType:    { type: String,  default: '' },
    photos:       { type: Array,   default: () => [] },
    eventDate:    { type: String,  default: '' },
    eventTime:    { type: String,  default: '' },
    location:     { type: String,  default: '' },
    dressCode:    { type: String,  default: '' },
    primaryColor: { type: String,  default: '' },
    bgImage:      { type: String,  default: '' },
    bgOpacity:    { type: Number,  default: 0.95 },
    whiteLabel:   { type: Boolean, default: false },
    isOwner:      { type: Boolean, default: false },
    eventId:      { type: String,  default: '' },
    rsvpCode:     { type: String,  default: '' },
    hasRsvp:      { type: Boolean, default: false },
    autoOpenPix:  { type: Boolean, default: false },
    openCartSheet: { type: Boolean, default: false },
  },

  data() {
    return {
      showGiftPanel: false,
      scrollTop: 0,
      isScrollable: false,
      scrollbarDragging: false,
      scrollbarDragStartY: 0,
      scrollbarDragStartScrollTop: 0,
      spinnerVisible: true,
      cardVisible: false,
      lightboxOpen: false,
      lbIndex: 0,
      lbSlideDir: 'ci-lb-next',
      lbTouchX: null,
      confetti: [],
      pixSheetOpen: false,
      pixStep: 'form',   // 'form' | 'qr' | 'success'
      pixLoading: false,
      pixQRBase64: '',
      pixKey: '',
      pixPaymentId: '',
      pixPollInterval: null,
      pixCountdownInterval: null,
      pixPollingActive: false,
      pixCountdown: 15,
      pixFormError: '',
      pixForm: { name: '', cpf: '', email: '', valueRaw: '' },
      pixLivreCustomer: null,
      pixLivreValue: 0,
      pixLivreItems: [],
      valueLimits: {
        min_free_pix_amount: 50,
        max_free_pix_amount: 5000,
        max_cart_amount: 20000,
      },
      // RSVP
      rsvpSheetOpen: false,
      rsvpStep: 'code',      // 'code' | 'confirm' | 'loading' | 'success'
      rsvpGroupName: '',
      rsvpInviteCount: 0,
      rsvpConfirmedCount: 1,
      rsvpMessage: '',
      rsvpError: '',
      rsvpCodeInput: '',
      rsvpCodeLookingUp: false,
      cartSheetOpen: false,
      cartStep: 'summary',      // 'summary' | 'identification' | 'pix-payment' | 'card-payment'
      cartPaymentMethod: 'pix', // 'pix' | 'card'
      cartCustomer: null,
      cartCloseConfirm: false,
      cartCardState: 'form', // 'form' | 'processing' | 'success'
      pixCloseConfirm: false,
    };
  },

  computed: {
    resolvedColor() {
      return this.primaryColor || '#ff50a3';
    },
    cssVars() {
      return { '--ci-primary': this.resolvedColor };
    },
    cartCloseConfirmMessage() {
      if (this.cartStep === 'card-payment') {
        return 'A verificação do cartão ainda está em andamento. Sair agora pode interromper a confirmação do pagamento.';
      }
      return 'O QR Code gerado será perdido e você precisará gerar um novo.';
    },
    scrollThumbStyle() {
      const el = this.$refs.ciEl;
      if (!el || el.scrollHeight <= el.clientHeight) return { height: '40px', transform: 'translateY(0)' };
      const trackH = el.clientHeight - 16;
      const ratio = el.clientHeight / el.scrollHeight;
      const thumbH = Math.max(40, trackH * ratio);
      const maxTop = trackH - thumbH;
      const scrolled = el.scrollHeight - el.clientHeight;
      const top = scrolled > 0 ? (this.scrollTop / scrolled) * maxTop : 0;
      return { height: thumbH + 'px', transform: `translateY(${top}px)` };
    },
    mainPhoto() {
      return this.photos[0] || '';
    },
    allPhotos() {
      return this.photos;
    },
    cartItems() {
      if (!this.eventId) return [];
      const cart = useCartStore();
      return cart.getItemsByEvent(this.eventId) || [];
    },
    cartTotal() {
      return this.cartItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
    },
    dateParts() {
      if (!this.eventDate) return null;
      const [year, month, day] = this.eventDate.split('-');
      const months = ['JANEIRO','FEVEREIRO','MARÇO','ABRIL','MAIO','JUNHO','JULHO',
                      'AGOSTO','SETEMBRO','OUTUBRO','NOVEMBRO','DEZEMBRO'];
      const weekdays = ['DOMINGO','SEGUNDA','TERÇA','QUARTA','QUINTA','SEXTA','SÁBADO'];
      const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      let time = '';
      if (this.eventTime) {
        const [h, m] = this.eventTime.split(':');
        time = `${h}:${m || '00'}`;
      }
      return {
        day:     String(parseInt(day)),
        month:   months[parseInt(month) - 1],
        year,
        weekday: weekdays[d.getDay()],
        time,
      };
    },
  },

  mounted() {
    this._fetchValueLimits();
    // Remove tag anterior (pode sobrar de HMR)
    const existing = document.getElementById('ci-global-styles');
    if (existing) existing.remove();
    this._styleTag = document.createElement('style');
    this._styleTag.id = 'ci-global-styles';
    this._styleTag.textContent = `
      html, body {
        overflow: hidden !important;
        padding-right: 0 !important;
        margin-right: 0 !important;
        scrollbar-width: none !important;
      }
      html::-webkit-scrollbar, body::-webkit-scrollbar { display: none !important; }
      .ci { scrollbar-width: none !important; scrollbar-gutter: auto !important; }
      .ci::-webkit-scrollbar { width: 0 !important; display: none !important; }
    `;
    document.head.appendChild(this._styleTag);
    setTimeout(() => { this.spinnerVisible = false; }, 500);
    setTimeout(() => { this._spawnConfetti(); }, 900);
    // Pré-preenche o código se veio na URL (usuário ainda valida ao clicar)
    if (this.rsvpCode) {
      this.rsvpCodeInput = this.rsvpCode.toUpperCase();
    }
    // Abre direto no Pix se solicitado
    if (this.autoOpenPix) {
      this.$nextTick(() => { this.pixSheetOpen = true; });
    }
    // Abre direto no cart se solicitado
    if (this.openCartSheet) {
      this.$nextTick(() => { this.cartSheetOpen = true; });
    }
    // Sincroniza confirmação persistida no cache com o backend (se eventId já estiver disponível)
    if (this.eventId) this._syncRsvpCache(this.eventId);
    this.$nextTick(() => { if (this.eventId) this._syncRsvpCache(this.eventId); });
    // Scrollbar customizado
    this._scrollHandler = () => {
      const el = this.$refs.ciEl;
      if (!el) return;
      this.scrollTop = el.scrollTop;
      this.isScrollable = el.scrollHeight > el.clientHeight + 2;
    };
    this.$nextTick(() => {
      const el = this.$refs.ciEl;
      if (el) el.addEventListener('scroll', this._scrollHandler, { passive: true });
    });
  },

  beforeUnmount() {
    if (this._styleTag) document.head.removeChild(this._styleTag);
    const el = this.$refs.ciEl;
    if (el && this._scrollHandler) el.removeEventListener('scroll', this._scrollHandler);
    window.removeEventListener('pointermove', this._onScrollbarPointerMove);
    this._stopPixIntervals();
  },

  watch: {
    pixSheetOpen(val) {
      if (val) this._openPixSheet();
      else this._closePixSheet();
    },
    showGiftPanel() {
      this.scrollTop = 0;
      this.isScrollable = false;
      this.$nextTick(() => this._refreshScrollState());
      setTimeout(() => this._refreshScrollState(), 250);
      setTimeout(() => this._refreshScrollState(), 700);
    },
    openCartSheet(val) {
      if (val) this.cartSheetOpen = true;
    },
    eventId(id) {
      if (!id) return;
      this._syncRsvpCache(id);
    },
  },

  methods: {
    formatCurrency(value) {
      return formatCurrency(value);
    },
    trackCheckoutMetric(eventName, payload = {}) {
      if (typeof window === 'undefined') return;

      const detail = {
        event: eventName,
        event_id: this.eventId,
        occurred_at: new Date().toISOString(),
        ...payload,
      };

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(detail);
      }
      window.dispatchEvent(new CustomEvent('checkout-telemetry', { detail }));
    },
    getPixLivreMetricTotal() {
      const currentValue = Number(this.pixLivreValue) || 0;
      if (currentValue > 0) return currentValue;

      return parseFloat(String(this.pixForm.valueRaw || '').replace(/\./g, '').replace(',', '.')) || 0;
    },
    // ── Cart sheet ────────────────────────────────────────────────────
    tryCloseCartSheet() {
      if (this.cartStep === 'pix-payment' || this.shouldProtectCardClose()) {
        this.cartCloseConfirm = true;
      } else {
        this.closeCartSheet();
      }
    },
    forceCloseCartSheet() {
      if (this.cartStep === 'pix-payment' || this.cartStep === 'card-payment') {
        this.trackCheckoutMetric('checkout_abandoned', {
          flow: 'cart',
          method: this.cartStep === 'card-payment' ? 'card' : 'pix',
          status: this.cartStep === 'card-payment' ? this.cartCardState : 'qr_active',
          total: this.cartTotal,
        });
      }
      this.cartCloseConfirm = false;
      this.closeCartSheet();
    },
    closeCartSheet() {
      this.cartSheetOpen = false;
      this.cartStep = 'summary';
      this.cartCustomer = null;
      this.cartCloseConfirm = false;
      this.cartCardState = 'form';
      this.$emit('cart-sheet-close');
    },
    goToIdentification(method) {
      const maxCart = Number(this.valueLimits.max_cart_amount) || 20000;
      if (this.cartTotal > maxCart) {
        toast.error(`O valor máximo por carrinho é de R$ ${formatCurrency(maxCart)}.`, { autoClose: 3000, position: 'top-right' });
        return;
      }
      this.cartPaymentMethod = method;
      this.cartStep = 'identification';
    },
    onCartIdentified(customer) {
      this.cartCustomer = customer;
      if (this.cartPaymentMethod === 'pix') {
        this.cartStep = 'pix-payment';
      } else {
        this.cartCardState = 'form';
        this.cartStep = 'card-payment';
      }
    },
    onCartCardSuccess() {
      const cart = useCartStore();
      cart.clearEvent(this.eventId);
      this.cartCardState = 'success';
      this.cartStep = 'pix-success';
    },
    onCartCardStateChange(state) {
      this.cartCardState = state || 'form';
    },
    shouldProtectCardClose() {
      return this.cartStep === 'card-payment' && ['processing', 'success'].includes(this.cartCardState);
    },
    goBackFromCardPayment() {
      if (this.shouldProtectCardClose()) {
        this.cartCloseConfirm = true;
        return;
      }
      this.cartStep = 'identification';
    },
    onCartSwitchToPix() {
      this.cartPaymentMethod = 'pix';
      this.cartCardState = 'form';
      this.cartStep = 'identification';
    },
    onCartPixSuccess() {
      const cart = useCartStore();
      cart.clearEvent(this.eventId);
      this.cartStep = 'pix-success';
    },

    onPixLivreSuccess() {
      const cart = useCartStore();
      cart.clearEvent(this.eventId);
      this.pixStep = 'success';
    },

    // ── Pix Contribuição Livre ────────────────────────────────────────
    onPixLivreIdentified({ name, email, cpf_cnpj, value }) {
      this.pixLivreCustomer = {
        name:     name  || 'Anônimo',
        email:    email || `anonimo+${Date.now()}@convidado.com`,
        cpf_cnpj,
        phone:    '',
      };
      this.pixLivreValue = value;
      this.pixLivreItems = [{ id: this.eventId, price: value, quantity: 1 }];
      this.pixStep = 'qr';
    },
    // ── RSVP ──────────────────────────────────────────────────────────
    async _syncRsvpCache(id) {
      try {
        const d = JSON.parse(localStorage.getItem(`rsvp-confirmed-${id}`) || '{}');
        const code = String(d.code || this.rsvpCode || '').trim().toUpperCase();
        if (!d.confirmed || !code) return;

        const { data } = await api.get(`/events/${id}/rsvp/${code}`);
        if (data.status === 'confirmed') {
          this.rsvpStep      = 'success';
          this.rsvpSheetOpen = false;
          this.rsvpGroupName = data.group_name || '';
          this.$emit('rsvp-confirmed', { eventId: id, code });
        } else {
          localStorage.removeItem(`rsvp-confirmed-${id}`);
        }
      } catch (_) {
        localStorage.removeItem(`rsvp-confirmed-${id}`);
      }
    },

    _openRsvpSheet() {
      this.rsvpStep      = 'code';
      this.rsvpGroupName = '';
      this.rsvpError     = '';
      this.rsvpMessage   = '';
      // pré-preenche se veio pela URL, mas mantém editável
      this.rsvpCodeInput = this.rsvpCode ? this.rsvpCode.toUpperCase() : '';
      this.rsvpSheetOpen = true;
    },

    async _lookupRsvpCode() {
      const code = this.rsvpCodeInput.trim().toUpperCase();
      if (!code) return;
      this.rsvpCodeLookingUp = true;
      this.rsvpError = '';
      try {
        const { data } = await api.get(`/events/${this.eventId}/rsvp/${code}`);
        this.rsvpGroupName      = data.group_name || '';
        this.rsvpInviteCount    = data.invite_count || 0;
        this.rsvpConfirmedCount = data.invite_count || 1;
        if (data.status === 'confirmed') {
          this.rsvpStep = 'success';
        } else {
          this.rsvpStep = 'confirm';
        }
      } catch (_) {
        this.rsvpError = 'Código não encontrado. Verifique e tente novamente.';
      } finally {
        this.rsvpCodeLookingUp = false;
      }
    },

    async _confirmarRsvp() {
      this.rsvpStep  = 'loading';
      this.rsvpError = '';
      const code = this.rsvpCodeInput.trim().toUpperCase();
      try {
        await api.post(`/events/${this.eventId}/rsvp/${code}/confirm`, {
          confirmed_count: this.rsvpConfirmedCount,
          message: this.rsvpMessage.trim() || null,
        });
        this.rsvpStep = 'success';
        this.rsvpSheetOpen = false;
        localStorage.setItem(`rsvp-confirmed-${this.eventId}`, JSON.stringify({ confirmed: true, code }));
        this.$emit('rsvp-confirmed', { eventId: this.eventId, code });
      } catch (err) {
        this.rsvpError = err.response?.data?.message || 'Não foi possível confirmar. Tente novamente.';
        this.rsvpStep  = 'confirm';
      }
    },

    _closeRsvpSheet() {
      this.rsvpSheetOpen = false;
    },

    // ── Pix Convite ────────────────────────────────────────────────────
    _openPixSheet() {
      this.pixStep = 'form';
      this.pixLivreCustomer = null;
      this.pixLivreValue = 0;
      this.pixLivreItems = [];
    },

    _tryClosePixSheet() {
      if (this.pixStep === 'qr') {
        this.pixCloseConfirm = true;
      } else {
        this._closePixSheet();
      }
    },
    _forceClosePixSheet() {
      if (this.pixStep === 'qr') {
        this.trackCheckoutMetric('checkout_abandoned', {
          flow: 'free_pix',
          method: 'pix',
          payment_id: this.pixPaymentId || null,
          status: 'qr_active',
          total: this.getPixLivreMetricTotal(),
        });
      }
      this.pixCloseConfirm = false;
      this._closePixSheet();
    },
    _closePixSheet() {
      this._stopPixIntervals();
      this.pixCloseConfirm = false;
      this.pixSheetOpen = false;
    },

    _voltarForm() {
      this._stopPixIntervals();
      this.pixStep = 'form';
    },

    async _gerarPixConvite() {
      this.pixFormError = '';
      const valor = parseFloat(this.pixForm.valueRaw.replace(/\./g, '').replace(',', '.'));
      const minPix = Number(this.valueLimits.min_free_pix_amount) || 50;
      const maxPix = Number(this.valueLimits.max_free_pix_amount) || 5000;
      if (!valor || valor < minPix) {
        this.pixFormError = `A contribuição mínima é de R$ ${formatCurrency(minPix)}.`;
        return;
      }
      if (valor > maxPix) {
        this.pixFormError = `A contribuição máxima é de R$ ${formatCurrency(maxPix)}.`;
        return;
      }
      const cpfDigits = this.pixForm.cpf.replace(/\D/g, '');
      if (cpfDigits.length !== 11) {
        this.pixFormError = 'Informe o CPF completo (11 dígitos).';
        return;
      }
      if (!this._validarCPF(cpfDigits)) {
        this.pixFormError = 'CPF inválido. Verifique e tente novamente.';
        return;
      }
      const name  = this.pixForm.name.trim()  || 'Anônimo';
      const email = this.pixForm.email.trim() || `anonimo+${Date.now()}@convidado.com`;
      const cpf   = cpfDigits;

      this.pixLoading = true;
      try {
        const { data } = await api.post(`/payments/pix/${this.eventId}`, {
          event_id:          this.eventId,
          method:            'pix',
          items:             [{ id: this.eventId, price: valor, quantity: 1 }],
          total:             valor,
          customer_name:     name,
          customer_cpf_cnpj: cpf,
          customer_email:    email,
        });

        this.pixQRBase64  = data.qrcode_image;
        this.pixKey       = data.pix_key;
        this.pixPaymentId = data.id;

        // Mesma chave do fluxo normal — compartilhado com a lista de presentes
        localStorage.setItem(`pixData-${this.eventId}`, JSON.stringify({
          qrcode_image: data.qrcode_image,
          pix_key:      data.pix_key,
          payment_id:   data.id,
        }));
        // Salva dados do form para reusar (sem CPF por privacidade)
        localStorage.setItem(`invitePixForm-${this.eventId}`, JSON.stringify({
          name:     this.pixForm.name,
          email:    this.pixForm.email,
          valueRaw: this.pixForm.valueRaw,
        }));

        this.pixStep = 'qr';
        this._startPixPoll();
      } catch (err) {
        const msg = err?.response?.data?.error || 'Não foi possível gerar o Pix. Tente novamente.';
        this.pixFormError = msg;
      } finally {
        this.pixLoading = false;
      }
    },

    async _fetchValueLimits() {
      try {
        const config = await getFees();
        const limits = config?.limits || {};
        this.valueLimits = {
          min_free_pix_amount: Number(limits.min_free_pix_amount ?? limits.free_pix?.min_amount ?? this.valueLimits.min_free_pix_amount),
          max_free_pix_amount: Number(limits.max_free_pix_amount ?? limits.free_pix?.max_amount ?? this.valueLimits.max_free_pix_amount),
          max_cart_amount: Number(limits.max_cart_amount ?? limits.cart?.max_amount ?? this.valueLimits.max_cart_amount),
        };
      } catch (error) {
        console.warn('Não foi possível carregar limites de valores.', error);
      }
    },

    _startPixPoll() {
      const INTERVAL = 15;
      this._stopPixIntervals();
      this.pixCountdown     = INTERVAL;
      this.pixPollingActive = true;

      this.pixCountdownInterval = setInterval(() => {
        if (this.pixCountdown > 0) this.pixCountdown--;
      }, 1000);

      this.pixPollInterval = setInterval(async () => {
        this.pixPollingActive = false;
        try {
          const { data } = await api.get(`/payments/status/${this.pixPaymentId}`);
          if (data.status === 'paid') {
            this._stopPixIntervals();
            localStorage.removeItem(`pixData-${this.eventId}`);
            localStorage.removeItem(`invitePixForm-${this.eventId}`);
            this.pixStep = 'success';
            return;
          }
        } catch (_) { /* silencioso */ } finally {
          this.pixCountdown     = INTERVAL;
          this.pixPollingActive = true;
        }
      }, INTERVAL * 1000);
    },

    _onPanelEntered() {
      this._refreshScrollState();
    },

    _refreshScrollState() {
      const el = this.$refs.ciEl;
      if (!el) return;
      this.scrollTop = el.scrollTop;
      this.isScrollable = el.scrollHeight > el.clientHeight + 2;
    },

    _scrollToScrollbarPointer(pointerY) {
      const el = this.$refs.ciEl;
      if (!el) return;
      const trackTop = 8;
      const trackH = Math.max(1, el.clientHeight - 16);
      const ratio = el.clientHeight / el.scrollHeight;
      const thumbH = Math.max(40, trackH * ratio);
      const maxThumbTop = Math.max(1, trackH - thumbH);
      const nextThumbTop = Math.min(Math.max(pointerY - trackTop - thumbH / 2, 0), maxThumbTop);
      const maxScrollTop = el.scrollHeight - el.clientHeight;
      el.scrollTop = (nextThumbTop / maxThumbTop) * maxScrollTop;
    },

    _onScrollbarTrackPointerDown(event) {
      event.preventDefault();
      this._scrollToScrollbarPointer(event.clientY);
    },

    _onScrollbarThumbPointerDown(event) {
      event.preventDefault();
      const el = this.$refs.ciEl;
      if (!el) return;
      this.scrollbarDragging = true;
      this.scrollbarDragStartY = event.clientY;
      this.scrollbarDragStartScrollTop = el.scrollTop;
      window.addEventListener('pointermove', this._onScrollbarPointerMove);
      window.addEventListener('pointerup', this._onScrollbarPointerUp, { once: true });
    },

    _onScrollbarPointerMove(event) {
      if (!this.scrollbarDragging) return;
      const el = this.$refs.ciEl;
      if (!el) return;
      const trackH = Math.max(1, el.clientHeight - 16);
      const ratio = el.clientHeight / el.scrollHeight;
      const thumbH = Math.max(40, trackH * ratio);
      const maxThumbTop = Math.max(1, trackH - thumbH);
      const maxScrollTop = el.scrollHeight - el.clientHeight;
      const deltaY = event.clientY - this.scrollbarDragStartY;
      el.scrollTop = this.scrollbarDragStartScrollTop + (deltaY / maxThumbTop) * maxScrollTop;
    },

    _onScrollbarPointerUp() {
      this.scrollbarDragging = false;
      window.removeEventListener('pointermove', this._onScrollbarPointerMove);
    },

    _stopPixIntervals() {
      if (this.pixPollInterval)      { clearInterval(this.pixPollInterval);      this.pixPollInterval      = null; }
      if (this.pixCountdownInterval) { clearInterval(this.pixCountdownInterval); this.pixCountdownInterval = null; }
      this.pixPollingActive = false;
    },

    _copyPixKey() {
      const text = this.pixKey;
      if (!text) return;
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => toast.success('Chave Pix copiada!'))
          .catch(() => this._copyFallback(text));
      } else {
        this._copyFallback(text);
      }
    },

    _copyFallback(text) {
      let ta;
      try {
        ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy');
        toast.success('Chave Pix copiada!');
      } catch (_) {
        toast.error('Erro ao copiar. Copie manualmente.');
      } finally {
        if (ta?.parentNode) ta.parentNode.removeChild(ta);
      }
    },

    _validarCPF(cpf) {
      if (/^(\d)\1{10}$/.test(cpf)) return false; // sequências repetidas ex: 111.111.111-11
      let sum = 0;
      for (let i = 0; i < 9; i++) sum += parseInt(cpf[i]) * (10 - i);
      let r = (sum * 10) % 11;
      if (r === 10 || r === 11) r = 0;
      if (r !== parseInt(cpf[9])) return false;
      sum = 0;
      for (let i = 0; i < 10; i++) sum += parseInt(cpf[i]) * (11 - i);
      r = (sum * 10) % 11;
      if (r === 10 || r === 11) r = 0;
      return r === parseInt(cpf[10]);
    },

    _maskCPF(e) {
      let v = e.target.value.replace(/\D/g, '').slice(0, 11);
      v = v.replace(/(\d{3})(\d)/, '$1.$2')
           .replace(/(\d{3})(\d)/, '$1.$2')
           .replace(/(\d{3})(\d{1,2})$/, '$1-$2');

      this.pixForm.cpf = v;
    },

    _maskCurrency(e) {
      let v = e.target.value.replace(/\D/g, '');
      if (!v) { this.pixForm.valueRaw = ''; return; }
      v = (parseInt(v, 10) / 100).toFixed(2)
            .replace('.', ',')
            .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      this.pixForm.valueRaw = v;
    },

    // ── Lightbox ────────────────────────────────────────────────────
    openLightbox(index) {
      this.lbIndex = index;
      this.lightboxOpen = true;
    },
    lbPrev() {
      this.lbSlideDir = 'ci-lb-prev';
      this.lbIndex = (this.lbIndex - 1 + this.allPhotos.length) % this.allPhotos.length;
    },
    lbNext() {
      this.lbSlideDir = 'ci-lb-next';
      this.lbIndex = (this.lbIndex + 1) % this.allPhotos.length;
    },
    lbGoto(i) {
      this.lbSlideDir = i > this.lbIndex ? 'ci-lb-next' : 'ci-lb-prev';
      this.lbIndex = i;
    },
    lbTouchStart(e) {
      this.lbTouchX = e.changedTouches[0].clientX;
    },
    lbTouchEnd(e) {
      if (this.lbTouchX === null) return;
      const dx = e.changedTouches[0].clientX - this.lbTouchX;
      if (Math.abs(dx) > 40) dx < 0 ? this.lbNext() : this.lbPrev();
      this.lbTouchX = null;
    },
    _spawnConfetti() {
      const colors = [
        'var(--ci-primary, #ff50a3)', '#fff', '#ffe066',
        '#a8edea', '#fed6e3', '#f9c6d0',
      ];
      const shapes = ['circle', 'rect', 'rect'];
      this.confetti = Array.from({ length: 48 }, (_, i) => {
        const color  = colors[i % colors.length];
        const shape  = shapes[i % shapes.length];
        const left   = (Math.random() * 100).toFixed(1);
        const delay  = (Math.random() * 1.2).toFixed(2);
        const dur    = (2.2 + Math.random() * 1.8).toFixed(2);
        const size   = (6 + Math.random() * 7).toFixed(1);
        const rotate = Math.floor(Math.random() * 360);
        return {
          id: i,
          style: {
            left:            `${left}%`,
            width:           `${size}px`,
            height:          shape === 'circle' ? `${size}px` : `${parseFloat(size) * 0.45}px`,
            borderRadius:    shape === 'circle' ? '50%' : '2px',
            background:      color,
            animationDelay:  `${delay}s`,
            animationDuration:`${dur}s`,
            transform:       `rotate(${rotate}deg)`,
          },
        };
      });
      // Remove após as animações terminarem
      setTimeout(() => { this.confetti = []; }, 5000);
    },
  },
};
</script>

<style scoped>
/* ── Container fullscreen ───────────────────── */
.ci {
  position: fixed;
  inset: 0;
  z-index: 9999;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 0;
  margin: 0;
  width: 100vw;
  height: 100dvh;
  background: #0d0d0d;
  scrollbar-width: none;
}
.ci::-webkit-scrollbar { display: none; width: 0; }

/* ── Scrollbar customizada ───────────────────── */
.ci__scrollbar {
  position: fixed;
  top: 8px;
  right: 4px;
  bottom: 8px;
  width: 8px;
  z-index: 10001;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.28);
  cursor: pointer;
  touch-action: none;
}
.ci__scrollbar--hidden {
  opacity: 0;
}
.ci__scrollbar-thumb {
  position: absolute;
  top: 0;
  left: 1px;
  width: 100%;
  width: 6px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 999px;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.24), 0 4px 14px rgba(0,0,0,0.28);
  cursor: grab;
  transition: transform 0.05s linear;
}
.ci__scrollbar-thumb:active { cursor: grabbing; }

@media (max-width: 767.98px), (pointer: coarse) {
  .ci__scrollbar {
    display: none;
  }
}

/* ── Loader ─────────────────────────────────── */
.ci__loader {
  position: fixed;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(2px);
}
.ci__spinner {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  border: 4px solid #e0e0e0;
  border-top-color: var(--ci-primary, #ff50a3);
  animation: ci-spin 0.75s linear infinite;
}
@keyframes ci-spin { to { transform: rotate(360deg); } }

/* ── Cena (fullscreen) ───────────────────────── */
.ci__scene {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 100dvh;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

/* Modo lista: alinha ao topo para crescer com o conteúdo */
.ci__scene--gift {
  align-items: flex-start;
  padding-top: max(2rem, 6dvh);
}

/* ── Foto de fundo ───────────────────────────── */
.ci__bg {
  position: fixed;
  inset: 0;
  overflow: hidden;
  z-index: 0;
}
.ci__bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}
.ci__bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0,0,0,0.20) 0%,
    rgba(0,0,0,0.45) 35%,
    rgba(0,0,0,0.75) 60%,
    rgba(0,0,0,0.92) 100%
  );
}

/* ── Corpo do convite ────────────────────────── */
.ci__body {
  position: relative;
  z-index: 1;
  width: 100%;
  transition: max-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding 0.4s ease;
}

.ci__body--gift {
  max-width: min(700px, 100%);
  padding: 2rem 1rem 1rem;
}

/* Keep title/badge centered when in gift mode */
.ci__body--gift .ci__badge,
.ci__body--gift .ci__title {
  text-align: center;
}

.ci__body {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 520px;
  padding: 2rem 2rem 2.5rem;
  color: #fff;
  text-align: center;
}

/* Badge tipo do evento */
.ci__badge {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #fff;
  background: rgba(0,0,0,0.35);
  border: 1.5px solid rgba(255,255,255,0.6);
  border-radius: 100px;
  padding: 0.25rem 0.85rem;
  margin-bottom: 1rem;
  backdrop-filter: blur(4px);
}

/* Título */
.ci__title {
  font-size: clamp(1.6rem, 5vw, 2.4rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin: 0 0 1rem;
  text-shadow: 0 2px 8px rgba(0,0,0,0.8), 0 4px 24px rgba(0,0,0,0.6);
}

/* Divisor */
.ci__divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  justify-content: center;
}
.ci__divider::before,
.ci__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255,255,255,0.25);
  max-width: 80px;
}
.ci__divider-gem {
  font-size: 0.6rem;
  color: var(--ci-primary, #ff50a3);
  opacity: 0.9;
}

/* Bloco de data estilizado */
.ci__datebox {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 1.25rem;
  background: rgba(0,0,0,0.30);
  border-radius: 14px;
  padding: 1rem 1.25rem;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255,255,255,0.08);
}

.ci__datebox-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  flex: 1;
}

.ci__datebox-col--center {
  flex: 0 0 auto;
  padding: 0 0.5rem;
}

.ci__datebox-sep {
  width: 1px;
  height: 42px;
  background: rgba(255,255,255,0.2);
  flex-shrink: 0;
}

.ci__datebox-month {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: rgba(255,255,255,0.9);
  text-transform: uppercase;
}
.ci__datebox-weekday {
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
}
.ci__datebox-day {
  font-size: 2.6rem;
  font-weight: 800;
  line-height: 1;
  color: #fff;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
}
.ci__datebox-time {
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--ci-primary, #ff50a3);
  text-transform: lowercase;
}
.ci__datebox-year {
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: rgba(255,255,255,0.85);
}

/* Detalhes (localização + dress code) */
.ci__details {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-bottom: 1.5rem;
  align-items: center;
  background: rgba(0,0,0,0.30);
  border-radius: 14px;
  padding: 0.85rem 1rem;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255,255,255,0.08);
}
.ci__detail {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: rgba(255,255,255,0.95);
  max-width: 340px;
  text-align: center;
  justify-content: center;
  line-height: 1.45;
  text-shadow: 0 1px 4px rgba(0,0,0,0.5);
}
.ci__detail-icon {
  color: #fff;
  font-size: 0.85rem;
  margin-top: 0.18rem;
  flex-shrink: 0;
  width: 16px;
  text-align: center;
  opacity: 0.85;
}

/* Galeria strip horizontal */
.ci__strip {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 1.75rem;
  border-radius: 12px;
  padding-bottom: 2px;
  scrollbar-width: none;
}
.ci__strip::-webkit-scrollbar { display: none; }

.ci__strip-cell {
  position: relative;
  flex: 0 0 88px;
  height: 88px;
  border-radius: 10px;
  overflow: hidden;
  scroll-snap-align: start;
  cursor: pointer;
  /* Animação de entrada staggered */
  animation: ci-cell-in 0.45s cubic-bezier(.22,1,.36,1) both;
}
@keyframes ci-cell-in {
  from { opacity: 0; transform: translateY(20px) scale(0.88); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
}

.ci__strip-cell img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.25s;
}
.ci__strip-cell:hover img { transform: scale(1.06); }

.ci__strip-cell-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.1rem;
  opacity: 0;
  transition: opacity 0.2s, background 0.2s;
}
.ci__strip-cell:hover .ci__strip-cell-overlay {
  opacity: 1;
  background: rgba(0,0,0,0.35);
}

/* ── Confetes ─────────────────────────────────── */
.ci__confetti {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 2;
  overflow: hidden;
}
.ci__confetti-piece {
  position: absolute;
  top: -12px;
  opacity: 0;
  animation: ci-confetti-fall linear both;
}
@keyframes ci-confetti-fall {
  0%   { opacity: 1; transform: translateY(-10px)  rotate(0deg)   scaleX(1); }
  80%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(110vh)  rotate(720deg) scaleX(-1); }
}

/* ── Lightbox ─────────────────────────────────── */
.ci__lb {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background: rgba(0,0,0,0.95);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ci__lb-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
  background: rgba(255,255,255,0.12);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  color: #fff;
  font-size: 1.1rem;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background 0.2s;
}
.ci__lb-close:hover { background: rgba(255,255,255,0.22); }

/* ── Botão flutuante de personalização ──────────────────── */
.ci__fab-customize {
  position: absolute;
  bottom: 1.25rem;
  right: 1.25rem;
  z-index: 20;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.18);
  color: #fff;
  font-size: 1.15rem;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background 0.2s, transform 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ci__fab-customize:hover {
  background: rgba(255,255,255,0.30);
  transform: scale(1.08);
}

/* ── Rodapé de marca ────────────────────────── */
.ci__brand-footer {
  position: absolute;
  bottom: 0.6rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  color: rgba(255,255,255,0.55);
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
}
.ci__brand-footer__link {
  pointer-events: all;
  color: inherit;
  text-decoration: none;
}
.ci__brand-footer__link:hover strong {
  color: #fff;
}
.ci__brand-footer strong {
  color: rgba(255,255,255,0.75);
  font-weight: 700;
}
.ci__brand-footer__heart {
  color: #ff50a3;
  font-size: 0.65rem;
}

.ci__lb-img-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.ci__lb-img {
  max-width: 100%;
  max-height: 100dvh;
  object-fit: contain;
  display: block;
}

.ci__lb-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: rgba(255,255,255,0.12);
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background 0.2s;
}
.ci__lb-nav:hover { background: rgba(255,255,255,0.25); }
.ci__lb-nav--prev { left: 0.75rem; }
.ci__lb-nav--next { right: 0.75rem; }

.ci__lb-dots {
  position: absolute;
  bottom: 1.5rem;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 6px;
}
.ci__lb-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255,255,255,0.35);
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}
.ci__lb-dot--active {
  background: var(--ci-primary, #ff50a3);
  transform: scale(1.3);
}

.ci__lb-counter {
  position: absolute;
  top: 1.1rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.8rem;
  color: rgba(255,255,255,0.6);
  letter-spacing: 0.05em;
}

/* Transições lightbox */
.ci-lb-enter-active, .ci-lb-leave-active { transition: opacity 0.2s; }
.ci-lb-enter-from, .ci-lb-leave-to { opacity: 0; }

/* Slide entre fotos */
.ci-lb-next-enter-active, .ci-lb-next-leave-active,
.ci-lb-prev-enter-active, .ci-lb-prev-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.ci-lb-next-enter-from  { opacity: 0; transform: translateX(40px); }
.ci-lb-next-leave-to    { opacity: 0; transform: translateX(-40px); }
.ci-lb-prev-enter-from  { opacity: 0; transform: translateX(-40px); }
.ci-lb-prev-leave-to    { opacity: 0; transform: translateX(40px); }

/* CTA secundário Pix */
/* CTAs secundários wrapper */
.ci__secondary-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.75rem;
}
@media (min-width: 768px) {
  .ci__secondary-row {
    flex-direction: row;
    align-items: center;
  }
  .ci__secondary-row .ci__cta-pix,
  .ci__secondary-row .ci__rsvp-link,
  .ci__secondary-row .ci__rsvp-confirmed-badge {
    flex: 1;
    margin-top: 0;
  }
  /* Sem RSVP: Pix ocupa largura toda */
  .ci__secondary-row--no-rsvp .ci__cta-pix {
    flex: unset;
    width: 100%;
  }
}
.ci__cta-pix {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 0;  padding: 0.45rem 1rem;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 100px;
  background: none;
  color: rgba(255,255,255,0.45);
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: color 0.18s, border-color 0.2s;
}
.ci__cta-pix:hover { color: rgba(255,255,255,0.75); border-color: rgba(255,255,255,0.4); }

/* ── Bottom Sheet Pix ─────────────────────────── */
.ci__sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10200;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.ci__sheet {
  position: relative;
  width: 100%;
  max-width: 520px;
  background: #1a1a1a;
  border-radius: 24px 24px 0 0;
  padding: 2rem 1.75rem 2.5rem;
  text-align: center;
  color: #fff;
}

.ci__sheet-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255,255,255,0.08);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  color: rgba(255,255,255,0.6);
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;
}
.ci__sheet-close:hover { background: rgba(255,255,255,0.15); }

/* ── Overlay de confirmação de saída ── */
.ci__sheet-confirm-overlay {
  position: absolute;
  inset: 0;
  background: rgba(18, 18, 22, 0.96);
  border-radius: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  z-index: 10;
  text-align: center;
}
.ci__sheet-confirm-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.5rem;
}
.ci__sheet-confirm-sub {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.5);
  margin: 0 0 1.5rem;
  line-height: 1.5;
}
.ci__sheet-confirm-actions {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  width: 100%;
}
.ci__sheet-confirm-btn {
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.9rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s;
}
.ci__sheet-confirm-btn:active { opacity: 0.8; }
.ci__sheet-confirm-btn--danger {
  background: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.6);
}
.ci__sheet-confirm-btn--cancel {
  background: var(--ci-primary, #ff50a3);
  color: #fff;
}

.ci__sheet-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--ci-primary, #ff50a3);
  margin: 0 0 0.35rem;
}
.ci__sheet-title {
  font-size: 1.4rem;
  font-weight: 800;
  margin: 0 0 0.4rem;
}
.ci__sheet-sub {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.5);
  margin: 0 0 1.5rem;
}

.ci__sheet-qr-placeholder {
  width: 160px;
  height: 160px;
  border-radius: 16px;
  background: rgba(255,255,255,0.06);
  border: 2px dashed rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 5rem;
  color: rgba(255,255,255,0.15);
}

.ci__sheet-copy {
  display: block;
  width: 100%;
  padding: 0.85rem 1rem;
  border: 1.5px solid rgba(255,255,255,0.15);
  border-radius: 100px;
  background: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.5);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: not-allowed;
}
.ci__sheet-copy:not(:disabled) {
  color: #fff;
  cursor: pointer;
  border-color: rgba(255,255,255,0.35);
  background: rgba(255,255,255,0.1);
}
.ci__sheet-copy:not(:disabled):hover {
  background: rgba(255,255,255,0.18);
}

.ci__sheet-qr-wrap {
  display: flex;
  justify-content: center;
  margin: 0 auto 1.5rem;
}
.ci__sheet-qr-img {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  background: #fff;
  padding: 8px;
}

/* ── RSVP ───────────────────────────────────── */
.ci__rsvp-icon {
  font-size: 2.2rem;
  text-align: center;
  margin-bottom: 0.1rem;
}
.ci__rsvp-textarea {
  margin-bottom: 1.25rem;
  width: 100%;
  background: rgba(255,255,255,0.08);
  border: 1.5px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  color: #fff;
  padding: 0.65rem 0.85rem;
  font-size: 0.92rem;
  resize: none;
  outline: none;
  transition: border-color 0.2s;
}
.ci__rsvp-textarea::placeholder { color: rgba(255,255,255,0.35); }
.ci__rsvp-textarea:focus { border-color: var(--ci-primary, #ff50a3); }
/* Linha de busca de código */
.ci__rsvp-code-row {
  display: flex;
  gap: 0.5rem;
}
.ci__rsvp-code-row input {
  flex: 1;
  background: rgba(255,255,255,0.08);
  border: 1.5px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  color: #fff;
  padding: 0.65rem 0.85rem;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  outline: none;
  transition: border-color 0.2s;
}
.ci__rsvp-code-row input:focus { border-color: var(--ci-primary, #ff50a3); }
.ci__rsvp-code-row input::placeholder { color: rgba(255,255,255,0.3); letter-spacing: 0; font-weight: 400; font-size: 0.9rem; }
.ci__rsvp-code-btn {
  padding: 0 1.1rem;
  border: none;
  border-radius: 12px;
  background: var(--ci-primary, #ff50a3);
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
}
.ci__rsvp-code-btn:disabled { opacity: 0.4; cursor: default; }
/* Contador de pessoas — centralizado */
.ci__rsvp-count-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;
}
.ci__rsvp-count-btn {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.25);
  background: transparent;
  color: #fff;
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s;
}
.ci__rsvp-count-btn:hover { border-color: var(--ci-primary, #ff50a3); background: rgba(255,255,255,0.08); }
.ci__rsvp-count-val {
  font-size: 2rem;
  font-weight: 800;
  color: #fff;
  min-width: 2.5rem;
  text-align: center;
}
.ci__rsvp-count-max {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.45);
}
.ci__sheet-error--inline { margin-top: 0.25rem; margin-bottom: 0; }
.ci__sheet-copy--rsvp {
  background: linear-gradient(135deg, var(--ci-primary, #ff50a3) 0%, #c026d3 100%);
}

.ci__sheet-paid {  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 0;
  color: #fff;
  text-align: center;
}
.ci__sheet-paid-icon {
  font-size: 3rem;
  color: #22c55e;
  animation: ci-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes ci-pop {
  from { transform: scale(0.5); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
}
.ci__sheet-paid-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin-top: 1rem;
  margin-bottom: 0;
}
.ci__sheet-paid-sub {
  color: rgba(255,255,255,0.75);
  font-size: 0.9rem;
  margin-top: 0.25rem;
}
.ci__sheet-success-btn {
  margin-top: 1.5rem;
  display: block;
  width: 100%;
  padding: 0.85rem 1rem;
  border: none;
  border-radius: 100px;
  background: var(--ci-primary, #ff50a3);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: opacity 0.2s;
}
.ci__sheet-success-btn:hover { opacity: 0.85; }

.ci__sheet-polling {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.75rem;
  min-height: 1.4rem;
}

/* Transição sheet */
.ci-sheet-enter-active, .ci-sheet-leave-active {
  transition: opacity 0.25s ease;
}
.ci-sheet-enter-active .ci__sheet,
.ci-sheet-leave-active .ci__sheet {
  transition: transform 0.3s cubic-bezier(.22,1,.36,1);
}
.ci-sheet-enter-from { opacity: 0; }
.ci-sheet-enter-from .ci__sheet { transform: translateY(100%); }
.ci-sheet-leave-to { opacity: 0; }
.ci-sheet-leave-to .ci__sheet { transform: translateY(100%); }

/* ── Pix Form Step ──────────────────────────────────────────────── */
.ci__sheet-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 0 8px;
}
.ci__sheet-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ci__sheet-field label {
  font-size: 12px;
  color: rgba(255,255,255,0.6);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.ci__optional { font-size: 10px; color: rgba(255,255,255,0.35); margin-left: 4px; }
.ci__required { font-size: 10px; color: var(--ci-primary, #ff50a3); margin-left: 2px; }
.ci__sheet-field input {
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 10px;
  color: #fff;
  font-size: 15px;
  padding: 12px 14px;
  outline: none;
  transition: border-color 0.2s;
}
.ci__sheet-field input:focus { border-color: var(--ci-primary, #ff50a3); }
.ci__sheet-currency {
  font-size: 18px !important;
  font-weight: 700 !important;
  letter-spacing: 0.01em;
  width: 100%;
}
.ci__sheet-error {
  color: #ff6b6b;
  font-size: 13px;
  text-align: center;
  padding: 2px 0;
}
.ci__sheet-back {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.5);
  font-size: 13px;
  cursor: pointer;
  padding: 8px 0 0;
  text-align: center;
  text-decoration: underline;
}
.ci__sheet-back:hover { color: rgba(255,255,255,0.8); }

.ci__cta {
  display: block;
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid var(--ci-primary, #ff50a3);
  border-radius: 100px;
  background: rgba(0,0,0,0.25);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3), inset 0 0 0 0 var(--ci-primary, #ff50a3);
  transition: background 0.2s, transform 0.18s, box-shadow 0.2s;
}
.ci__cta:hover {
  background: rgba(0,0,0,0.45);
  transform: translateY(-1px);
}
.ci__cta:active {
  transform: translateY(0);
}
/* RSVP: link discreto com borda */
.ci__rsvp-link {
  background: none;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 100px;
  color: rgba(255,255,255,0.45);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.45rem 1rem;
  margin-top: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: 100%;
  transition: color 0.2s, border-color 0.2s;
}
.ci__rsvp-link:hover {
  color: rgba(255,255,255,0.75);
  border-color: rgba(255,255,255,0.4);
}
/* Secundário sempre visível */
.ci__cta--secondary {
  background: transparent;
  border-color: rgba(255,255,255,0.3);
  font-weight: 500;
  font-size: 0.9rem;
  padding: 0.85rem 1.5rem;
}
/* Badge presença confirmada */
.ci__rsvp-confirmed-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  flex: 1;
  padding: 0.45rem 1rem;
  border-radius: 100px;
  background: rgba(34,197,94,0.15);
  border: 1px solid rgba(34,197,94,0.4);
  color: #22c55e;
  font-weight: 600;
  font-size: 0.8rem;
  margin-top: 0;
}
/* Linha de busca de código */
.ci__rsvp-code-row {
  display: flex;
  gap: 0.5rem;
}
.ci__rsvp-code-row input {
  flex: 1;
  background: rgba(255,255,255,0.08);
  border: 1.5px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  color: #fff;
  padding: 0.65rem 0.85rem;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  outline: none;
  transition: border-color 0.2s;
}
.ci__rsvp-code-row input:focus { border-color: var(--ci-primary, #ff50a3); }
.ci__rsvp-code-row input::placeholder { color: rgba(255,255,255,0.3); letter-spacing: 0; font-weight: 400; }
.ci__rsvp-code-btn {
  padding: 0 1.1rem;
  border: none;
  border-radius: 12px;
  background: var(--ci-primary, #ff50a3);
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
}
.ci__rsvp-code-btn:disabled { opacity: 0.4; cursor: default; }
/* Contador de pessoas */
.ci__rsvp-count-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}
.ci__rsvp-count-btn {
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.25);
  background: transparent;
  color: #fff;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s;
}
.ci__rsvp-count-btn:hover { border-color: var(--ci-primary, #ff50a3); background: rgba(255,255,255,0.08); }
.ci__rsvp-count-val {
  font-size: 1.6rem;
  font-weight: 800;
  color: #fff;
  min-width: 2rem;
  text-align: center;
}
.ci__rsvp-count-max {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.45);
}

/* ── Transição convite ↔ lista ─────────────────── */
.ci-panel-slide-enter-active,
.ci-panel-slide-leave-active {
  transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.ci-panel-slide-enter-from {
  opacity: 0;
  transform: translateY(40px);
}
.ci-panel-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* ── Gift panel (lista de presentes) ────────── */
.ci__gift-panel {
  width: 100%;
}

.ci__gift-handle {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.6rem 0 0.9rem;
  cursor: pointer;
  gap: 0.3rem;
  user-select: none;
  transition: background 0.3s;
}
.ci__gift-handle-pill {
  width: 36px;
  height: 4px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.3);
  transition: background 0.2s;
}
.ci__gift-handle:hover .ci__gift-handle-pill {
  background: rgba(255, 255, 255, 0.7);
}
.ci__gift-handle-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.2s;
}
.ci__gift-handle:hover .ci__gift-handle-label {
  color: rgba(255, 255, 255, 0.9);
}

/* ── Cart Sheet ──────────────────────────────── */
.ci__sheet--cart {
  max-height: 85dvh;
  display: flex;
  flex-direction: column;
}

.ci__cart-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
  max-height: 36dvh;
  margin: 1rem 0;
  padding-right: 0.25rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.15) transparent;
}
.ci__cart-items::-webkit-scrollbar { width: 3px; }
.ci__cart-items::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 99px; }

.ci__cart-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 0.75rem;
  padding: 0.5rem;
}

.ci__cart-item-img {
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 0.5rem;
  flex-shrink: 0;
}

.ci__cart-item-img--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.3);
  font-size: 1.2rem;
}

.ci__cart-item-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
  text-align: left;
}

.ci__cart-item-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255,255,255,0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ci__cart-item-price {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--ci-primary, #ff50a3);
}

.ci__cart-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-top: 1px solid rgba(255,255,255,0.1);
  border-bottom: 1px solid rgba(255,255,255,0.1);
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
  color: rgba(255,255,255,0.7);
}

.ci__cart-total strong {
  color: #fff;
  font-size: 1.05rem;
  font-weight: 800;
}

.ci__cart-actions {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.ci__cart-btn {
  width: 100%;
  padding: 0.8rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.9rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.ci__cart-btn:active { transform: scale(0.97); opacity: 0.9; }

.ci__cart-btn--pix {
  background: var(--ci-primary, #ff50a3);
  color: #fff;
  box-shadow: 0 4px 16px color-mix(in srgb, var(--ci-primary, #ff50a3) 40%, transparent);
}

.ci__cart-btn--card {
  background: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.9);
  border: 1px solid rgba(255,255,255,0.15);
}
.ci__cart-btn--card:hover {
  background: rgba(255,255,255,0.16);
}

.ci__cart-back-btn {
  background: none;
  border: none;
  color: rgba(255,255,255,0.4);
  font-size: 0.78rem;
  cursor: pointer;
  padding: 0.5rem 0;
  text-align: left;
  transition: color 0.18s;
}
.ci__cart-back-btn:hover { color: rgba(255,255,255,0.7); }

/* ── Cenário sem foto: fundo gradiente de cor --*/
.ci__scene:not(:has(.ci__bg-img)) .ci__bg-overlay {
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--ci-primary, #ff50a3) 80%, #000) 0%,
    #1a0a12 100%
  );
}

/* ── Transições ─────────────────────────────── */
.ci-fade-leave-active { transition: opacity 0.3s ease; }
.ci-fade-leave-to     { opacity: 0; }

.ci-reveal-enter-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}
.ci-reveal-enter-from {
  opacity: 0;
  transform: scale(1.04);
}
</style>
